import React from "react";
import IntlProvider, {
  DEFAULT_LOCALE,
  resolveLocaleId,
} from "./locales/IntlProvider";

import DialogProvider from "./components/dialog/DialogProvider";

import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useOutletContext,
} from "react-router-dom";
import Dialog from "./components/dialog/Dialog";
import AddGroupDevicePopup from "./pages/device/AddGroupDevicePopup";
import DeviceMovePopup from "./pages/device/DeviceMovePopup";
import DeviceSettingsPopup from "./pages/device/DeviceSettingsPopup";
import EditGroupPopup from "./pages/group/EditGroupPopup";
import { LoadingStore } from "./store/loading/LoadingContextStore";
import UIState, { PathParamsSynchronizer } from "./store/ui/UIState";

import InfoScreen from "./pages/info_screen/InfoScreen";
import Main from "./pages/main/Main";
import { ThemeProvider } from "./store/ui/ThemeProvider";
import "./styles/main.scss";
import PageLayout, { PageLayoutOutletContext } from "./template/PageLayout";
import SvgFilters from "./template/SvgFilters";
import WebappPlatformApiWrapper from "./webapp-platform-api/WebappPlatformApiWrapper";

function DefaultRedirect() {
  const outlet = useOutletContext<PageLayoutOutletContext>();
  return <Navigate to={outlet.generatePath("/")} replace />;
}

function generatePathname(
  locale: string,
  relative_path: string,
  search = "",
  hash = ""
) {
  // remove duplicated and ending '/'
  const pathname = (locale + relative_path)
    .split("/")
    .filter((e) => e)
    .join("/");

  return "/" + pathname + search + hash;
}

function App() {
  // (?:\/([a-z]{2}(?:-Hans)?(?:-[A-Z]{2}?)?))? --- parse locale
  // (\/.*)? -- parse relative path
  const localeRegex = /^(?:\/([a-z]{2}(?:-Hans)?(?:-[A-Z]{2}?)?))?(\/.*)?$/;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const matches = localeRegex.exec(window.location.pathname);
  const pathLocale = matches?.[1];
  const pathname = matches?.[2] ?? "";
  const search = window.location.search;

  const locale = React.useMemo(() => {
    return (
      resolveLocaleId(pathLocale) ??
      resolveLocaleId(window.navigator.language) ??
      DEFAULT_LOCALE
    );
  }, [pathLocale]);

  return (
    <IntlProvider locale={locale} timezone={timezone}>
      <DialogProvider>
        <LoadingStore>
          <ThemeProvider>
            <SvgFilters />
            <Dialog />
            <WebappPlatformApiWrapper>
              <Router>
                <UIState>
                  <Routes>
                    <Route path={`/${locale}`} element={<PageLayout />}>
                      <Route path="info" element={<InfoScreen />} />
                      <Route path="*" element={<Main />}>
                        <Route index element={<></>} />
                        <Route
                          path="device/:deviceType/:deviceId"
                          element={<DeviceSettingsPopup />}
                        />
                        <Route
                          path="device/:deviceType/:deviceId/move"
                          element={<DeviceMovePopup />}
                        />
                        <Route
                          path="group/create"
                          element={<EditGroupPopup />}
                        />
                        <Route
                          path="group/:groupId"
                          element={<PathParamsSynchronizer />}
                        />
                        <Route
                          path="group/:groupId/edit"
                          element={<EditGroupPopup />}
                        />
                        <Route
                          path="group/:groupId/device"
                          element={<AddGroupDevicePopup />}
                        />
                        <Route path="*" element={<DefaultRedirect />} />
                      </Route>
                    </Route>
                    <Route
                      path="*"
                      element={
                        <Navigate
                          to={generatePathname(locale, pathname, search)}
                          replace
                        />
                      }
                    />
                  </Routes>
                </UIState>
              </Router>
            </WebappPlatformApiWrapper>
          </ThemeProvider>
        </LoadingStore>
      </DialogProvider>
    </IntlProvider>
  );
}

export default App;
